<template>
  <div class="services-tab">
    <b-container fluid>
      <!-- Section: Services Management -->
      <b-row class="mb-4">
        <b-col cols="12">
          <div class="section-header">
            <div class="header-top">
              <h3 class="section-title">
                <i class="fas fa-network-wired mr-2" />
                {{ $t('admcfg.servicesManagement', 'Services Management') }}
              </h3>
              <div class="header-actions">
                <b-button
                  size="sm"
                  variant="primary"
                  @click="showAddServiceModal = true"
                  class="mr-2"
                >
                  <i class="fas fa-plus mr-1" />
                  {{ $t('general.add', 'Add Service') }}
                </b-button>
                <b-button
                  size="sm"
                  variant="secondary"
                  @click="refreshAll"
                  :disabled="isLoading"
                >
                  <i class="fas fa-sync mr-1" :class="{ 'fa-spin': isLoading }" />
                  {{ $t('general.refresh', 'Refresh') }}
                </b-button>
              </div>
            </div>
            <p class="section-description">
              {{ $t('admcfg.servicesDescription', 'Monitor and manage ship connections to services') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Loading / Error States -->
      <b-row v-if="isLoading" class="mb-4">
        <b-col cols="12">
          <div class="text-center py-5">
            <b-spinner />
            <p class="mt-3">
              {{ $t('general.loading', 'Loading...') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <b-row v-else-if="errorMessage" class="mb-4">
        <b-col cols="12">
          <b-alert show variant="danger">
            <i class="fas fa-exclamation-circle mr-2" />
            {{ errorMessage }}
            <b-button
              size="sm"
              variant="outline-danger"
              class="float-right"
              @click="errorMessage = ''"
            >
              {{ $t('general.close', 'Close') }}
            </b-button>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Services Table -->
      <b-row v-if="!isLoading && devices.length > 0" class="mb-4">
        <b-col cols="12">
          <div class="services-table-wrapper">
            <b-table
              striped
              bordered
              hover
              :items="tableItems"
              :fields="tableFields"
              responsive
              class="services-table"
            >
              <template #cell(device_id)="data">
                <div class="device-id">
                  <i class="fas fa-ship mr-2" />
                  <code>{{ data.value }}</code>
                </div>
              </template>

              <template #cell(service_type)="data">
                <b-badge variant="primary">
                  {{ data.value }}
                </b-badge>
              </template>

              <template #cell(status)="data">
                <b-badge :variant="getStatusBadge(data.item.status)">
                  {{ getStatusInfo(data.item.status).icon }}
                  {{ data.value }}
                </b-badge>
              </template>

              <template #cell(end_date)="data">
                <span v-if="data.value === 'Unlimited'" class="text-muted">
                  {{ data.value }}
                </span>
                <span v-else :class="{ 'text-danger': isDateExpired(data.item.end_date_raw) }">
                  {{ data.value }}
                </span>
              </template>

              <template #cell(actions)="data">
                <div class="action-buttons">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    title="Edit"
                    @click="editService(data.item)"
                    class="mr-1"
                  >
                    <i class="fas fa-edit" />
                  </b-button>

                  <b-button
                    v-if="data.item.status === 'Active'"
                    size="sm"
                    variant="outline-warning"
                    title="Pause"
                    @click="pauseServiceAction(data.item)"
                    class="mr-1"
                  >
                    <i class="fas fa-pause" />
                  </b-button>

                  <b-button
                    v-if="data.item.status === 'Paused'"
                    size="sm"
                    variant="outline-success"
                    title="Resume"
                    @click="resumeServiceAction(data.item)"
                    class="mr-1"
                  >
                    <i class="fas fa-play" />
                  </b-button>

                  <b-button
                    size="sm"
                    variant="outline-danger"
                    title="Unsubscribe"
                    @click="unsubscribeAction(data.item)"
                  >
                    <i class="fas fa-trash" />
                  </b-button>
                </div>
              </template>
            </b-table>
          </div>
        </b-col>
      </b-row>

      <!-- Empty State -->
      <b-row v-else-if="!isLoading && devices.length === 0" class="mb-4">
        <b-col cols="12">
          <div class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-inbox" />
            </div>
            <h5>{{ $t('admcfg.noDevices', 'No devices found') }}</h5>
            <p class="text-muted">
              {{ $t('admcfg.noDevicesDescription', 'You have no devices yet. Subscribe to services to get started.') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Section: Service Health -->
      <b-row class="mt-5">
        <b-col cols="12">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-heartbeat mr-2" />
              {{ $t('admcfg.serviceHealth', 'Service Health') }}
            </h3>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="ENC Catalog"
            :status="servicesHealth.Catalog || 'error'"
            icon="fas fa-map"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="AIS Service"
            :status="servicesHealth.AIS || 'error'"
            icon="fas fa-satellite"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="Weather API"
            :status="servicesHealth.Weather || 'error'"
            icon="fas fa-cloud"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="Navarea Service"
            :status="servicesHealth.Navarea || 'error'"
            icon="fas fa-compass"
          />
        </b-col>
      </b-row>

      <!-- Add/Edit Service Modal -->
      <service-modal
        v-model="showAddServiceModal"
        :service-data="selectedService"
        @save="saveService"
      />
    </b-container>
  </div>
</template>

<script>
import HealthCard from '../components/HealthCard.vue'
import ServiceModal from '../components/ServiceModal.vue'
import { useServicesStore } from '@/stores/services'
import {
  getAllDevicesWithServices,
  pauseService,
  resumeService,
  unsubscribeFromService,
  getStatusInfo,
  isDateExpired,
  formatDate,
  getAllServicesHealth
} from '@/helpers/apiServices'

export default {
  name: 'ServicesTab',
  components: {
    HealthCard,
    ServiceModal
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      showAddServiceModal: false,
      selectedService: null,
      servicesHealth: {
        AIS: 'error',
        Navarea: 'error',
        Weather: 'error',
        Catalog: 'error'
      },
      healthCheckInterval: null,
      tableFields: [
        {
          key: 'device_id',
          label: 'Device ID',
          sortable: true
        },
        {
          key: 'service_type',
          label: 'Service',
          sortable: true
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true
        },
        {
          key: 'end_date',
          label: 'Expires',
          sortable: true
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false
        }
      ]
    }
  },
  computed: {
    servicesStore() {
      return useServicesStore()
    },
    devices() {
      return this.servicesStore.getAllDevices
    },
    storeError() {
      return this.servicesStore.error
    },
    tableItems() {
      const items = []
      this.devices.forEach(device => {
        if (device.services && device.services.length > 0) {
          device.services.forEach(service => {
            items.push({
              device_id: device.device_id,
              service_type: service.ServiceType,
              status: service.Status,
              end_date: formatDate(service.EndDate),
              end_date_raw: service.EndDate,
              notes: service.Notes,
              // Full data for actions
              id: service.Id,
              start_date: service.StartDate,
              raw_service: service
            })
          })
        }
      })
      return items
    }
  },
  watch: {
    storeError(newError) {
      if (newError) {
        this.errorMessage = newError
      }
    }
  },
  mounted() {
    // Загрузить данные при монтировании компоненты
    this.loadDevices()
    this.loadServicesHealth()

    // Установить интервал для периодической проверки health статуса (каждые 30 сек)
    this.healthCheckInterval = setInterval(() => {
      this.loadServicesHealth()
    }, 30000)
  },
  beforeUnmount() {
    // Очистить интервал при размонтировании
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
    }
  },
  destroyed() {
    // Дополнительная очистка для Vue 2 (в случае если beforeUnmount не срабатывает)
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
    }
  },
  methods: {
    async loadDevices() {
      this.isLoading = true
      this.errorMessage = ''
      this.servicesStore.setLoading(true)

      try {
        const devicesData = await getAllDevicesWithServices()
        this.servicesStore.setDevices(devicesData)
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to load devices'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
        console.error('Load error:', error)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async refreshAll() {
      // Обновить и devices и health статусы одновременно
      await Promise.all([
        this.loadDevices(),
        this.loadServicesHealth()
      ])
    },
    async loadServicesHealth() {
      try {
        const response = await getAllServicesHealth()
        if (response && response.services) {
          // Mapировать status -> компонента
          response.services.forEach(service => {
            const serviceKey = service.service_type
            // Преобразовать статус в нижний регистр
            const status = service.status?.toLowerCase() || 'error'
            this.servicesHealth[serviceKey] = status
          })
        }
      } catch (error) {
        console.error('Failed to load services health:', error)
        // При ошибке показать все как error
        Object.keys(this.servicesHealth).forEach(key => {
          this.servicesHealth[key] = 'error'
        })
      }
    },
    getStatusInfo(status) {
      return getStatusInfo(status)
    },
    getStatusBadge(status) {
      return this.getStatusInfo(status).badge
    },
    isDateExpired(date) {
      return isDateExpired(date)
    },
    editService(item) {
      this.selectedService = {
        ...item,
        isEdit: true
      }
      this.showAddServiceModal = true
    },
    async pauseServiceAction(item) {
      if (!confirm(`Pause ${item.service_type} for ${item.device_id}?`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await pauseService(item.device_id, item.service_type)
        await this.loadDevices()
        this.$bvToast.toast('Service paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to pause service'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async resumeServiceAction(item) {
      if (!confirm(`Resume ${item.service_type} for ${item.device_id}?`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await resumeService(item.device_id, item.service_type)
        await this.loadDevices()
        this.$bvToast.toast('Service resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to resume service'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async unsubscribeAction(item) {
      if (!confirm(`Unsubscribe from ${item.service_type} for ${item.device_id}? This action cannot be undone.`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await unsubscribeFromService(item.device_id, item.service_type)
        await this.loadDevices()
        this.$bvToast.toast('Service unsubscribed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to unsubscribe'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async saveService() {
      this.showAddServiceModal = false
      this.selectedService = null
      await this.loadDevices()
    }
  }
}
</script>

<style scoped>
.services-tab {
  padding: 20px 0;
}

.section-header {
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.section-title {
  color: #0066cc;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0;
}

.section-description {
  color: #666;
  font-size: 0.9rem;
  margin: 5px 0 0 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.services-table-wrapper {
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  overflow-x: auto;
}

.services-table {
  margin-bottom: 0;
}

.services-table thead th {
  background-color: #f8f9fa;
  color: #0066cc;
  font-weight: 600;
  border-color: #dee2e6;
}

.device-id {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
}

.device-id code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 3px;
  color: #d63384;
}

.action-buttons {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.action-buttons .btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 3rem;
  color: #ccc;
  margin-bottom: 15px;
}

.empty-state h5 {
  color: #333;
  font-weight: 600;
  margin-bottom: 10px;
}

.empty-state p {
  color: #666;
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .header-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .header-actions button {
    width: 100%;
  }

  .services-table {
    font-size: 0.85rem;
  }

  .action-buttons {
    justify-content: center;
  }
}
</style>
